import { estimateConstants as C } from '../actions/constants'

// ---- helpers ----
const pickDoc = (p) => p?.doc ?? p?.item ?? p?.data ?? p ?? null

const tsOf = (x) =>
  (Number.isFinite(x?._ts) ? x._ts : 0) ||
  (x?.updatedAt ? Date.parse(x.updatedAt) : 0) || 0

const revOf = (x) => (x?._rev ?? 0)

const isNewer = (next, prev) => {
  const tn = tsOf(next), tp = tsOf(prev)
  if (tn !== tp) return tn > tp
  return revOf(next) > revOf(prev)
}

const upsertById = (arr, doc) => {
  if (!doc?._id) return arr
  const i = arr.findIndex(x => x._id === doc._id)
  if (i === -1) return [doc, ...arr]
  const next = arr.slice()
  next[i] = { ...next[i], ...doc }
  return next
}

// ---- state ----
const init = {
  list: {
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    loading: false,
    error: null,
    params: {}
  },
  templates: {
    items: [],
    loading: false,
    error: null
  },
  current: {
    data: {
      meta: { company: 'ALOTB', date: new Date().toISOString().slice(0, 10), hideMarkup: false, markup: 10, discount: 0 },
      params: { screed_mm: 60, plinth_m: 42, doors: 5, elec_override: null },
      bathroom: { floorArea: 4, wetWallArea: 18, perim: 8 },
      rooms: [], materials: [], works: [], totals: { mat: 0, work: 0, mark: 0, all: 0 }
    },
    locking: {},
    loading: false,
    error: null
  }
}

export default function estimateReducer(state = init, action) {
  switch (action.type) {
    // ===== LIST =====
    case C.LIST_REQUEST:
      return {
        ...state,
        list: { ...state.list, loading: true, error: null, params: action.params || {} }
      }

    case C.LIST_SUCCESS: {
      const { items = [], total = 0, page = 1, limit = 50 } = action.payload || {}
      const sorted = items.slice().sort(
        (a, b) => Date.parse(b?.updatedAt || 0) - Date.parse(a?.updatedAt || 0)
      )
      return {
        ...state,
        list: { ...state.list, loading: false, error: null, items: sorted, total, page, limit }
      }
    }

    case C.LIST_FAILURE:
      return {
        ...state,
        list: { ...state.list, loading: false, error: action.error || 'LIST_FAILED' }
      }

    // ===== ONE =====
    case C.GET_REQUEST:
      return { ...state, current: { ...state.current, loading: true, error: null } }

    case C.GET_SUCCESS: {
      const doc = pickDoc(action.payload)
      return { ...state, current: { ...state.current, data: doc || state.current.data, loading: false, error: null } }
    }

    case C.GET_FAILURE:
      return { ...state, current: { ...state.current, loading: false, error: action.error || 'GET_FAILED' } }

    // ===== CREATE / UPDATE / DELETE (REST) =====
    case C.CREATE_SUCCESS: {
      const doc = pickDoc(action.payload)
      return {
        ...state,
        list: { ...state.list, items: upsertById(state.list.items, doc), total: Math.max(state.list.total + 1, 1) },
        current: { ...state.current, data: doc || state.current.data }
      }
    }

    case C.UPDATE_SUCCESS: {
      const doc = pickDoc(action.payload)
      const cur = state.current.data
      const nextCurrent = (!cur || isNewer(doc, cur)) ? doc : cur
      return {
        ...state,
        list: { ...state.list, items: upsertById(state.list.items, doc) },
        current: { ...state.current, data: nextCurrent }
      }
    }

    case C.DELETE_SUCCESS: {
      const id = action.payload?.estimateId
      const items = state.list.items.filter(x => x._id !== id)
      const next = { ...state, list: { ...state.list, items, total: Math.max(0, state.list.total - 1) } }
      if (state.current.data?._id === id) {
        next.current = { ...state.current, data: init.current.data }
      }
      return next
    }

    // ===== Templates =====
    case C.TEMPLATES_REQUEST:
      return { ...state, templates: { ...state.templates, loading: true, error: null } }

    case C.TEMPLATES_SUCCESS:
      return { ...state, templates: { items: action.payload || [], loading: false, error: null } }

    case C.TEMPLATES_FAILURE:
      return { ...state, templates: { ...state.templates, loading: false, error: action.error || 'TEMPLATES_FAILED' } }

    case C.APPLY_TEMPLATE_SUCCESS: {
      const doc = pickDoc(action.payload)
      return { ...state, current: { ...state.current, data: doc || state.current.data } }
    }

    // ===== Sockets (collab) =====
    case C.SOCKET_SNAPSHOT: {
      const incoming = pickDoc(action.payload)
      const cur = state.current.data
      const next = { ...state }

      if (incoming && (!cur || isNewer(incoming, cur))) {
        next.current = { ...state.current, data: incoming, loading: false }
      }
      if (incoming?._id) {
        next.list = { ...state.list, items: upsertById(state.list.items, incoming) }
      }
      return next
    }

    case C.SOCKET_UPDATED: {
      const patch = action.payload?.patch || {}
      const cur = state.current.data || {}
      const totals = action.payload?.totals
      if (!isNewer(patch, cur)) return state
      const merged = { ...cur, ...patch, ...(totals ? { totals } : {}) }
      const next = {
        ...state,
        current: { ...state.current, data: merged }
      }
      if (merged?._id) {
        next.list = { ...state.list, items: upsertById(state.list.items, merged) }
      }
      return next
    }

    case C.SOCKET_LOCKED:
      return {
        ...state,
        current: {
          ...state.current,
          locking: { ...(state.current.locking || {}), [action.payload?.field]: action.payload?.userId || true }
        }
      }

    case C.SOCKET_UNLOCKED: {
      const k = action.payload?.field
      const nextLock = { ...(state.current.locking || {}) }
      if (k) delete nextLock[k]
      return { ...state, current: { ...state.current, locking: nextLock } }
    }

    // ===== Local UI =====
    case C.SET_LOCAL: {
      const patch = action.payload || {}
      if (patch && patch._ts == null) patch._ts = Date.now() // анти-лаг для локальных правок
      const cur = state.current.data || {}
      const merged = { ...cur, ...patch }
      return { ...state, current: { ...state.current, data: merged } }
    }

    case C.SET_LOADING:
      return { ...state, current: { ...state.current, loading: !!action.payload } }

    default:
      return state
  }
}
